/**
 * Google Analytics 4 (GA4) Integration with Google Consent Mode v2
 * 
 * CRITICAL: Uses `arguments` object instead of spread operator
 * The spread operator creates a true Array which GA4's gtag.js
 * may misinterpret, causing silent failures.
 *
 * Event taxonomy registry: see js/cellcog/TRACKING.md (one section per page —
 * every new funnel event gets registered there before it ships).
 */

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-L73DCNH186';

let isInitialized = false;
let consentModeInitialized = false;
// True in the quiet (no-banner) bucket. There, ad measurement follows the analytics choice:
// on by default, off with a Reject (Cookie preferences) or GPC. Legal category 2 (Sep 29 2026).
let quietBucket = false;

// SPA route memory: document.referrer never updates on client-side navigation,
// so the previous in-app path is tracked here (fed by trackPageView, which is
// already called on every route change). Used for source-page attribution.
let previousPath = null;
let currentPath = typeof window !== 'undefined' ? window.location.pathname : null;

/**
 * Initialize Google Consent Mode v2
 * Must be called BEFORE loading GA4 script
 * 
 * @param {boolean} isEUUser - Whether user is in EU/EEA
 * @param {boolean} analyticsConsent - Whether user consented to analytics
 */
export function initializeConsentMode(isEUUser, analyticsConsent = false) {
  if (consentModeInitialized) {
    return;
  }

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;

  if (isEUUser) {
    // EU users: Default to denied, grant based on consent
    gtag('consent', 'default', {
      'analytics_storage': analyticsConsent ? 'granted' : 'denied',
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'wait_for_update': 500,
    });

  } else {
    // Quiet bucket (geo-split v3): analytics defaults granted, but an explicit
    // rejection (Cookie preferences) or a GPC signal arrives here as
    // analyticsConsent=false and must stick as denied.
    // Sep 29 2026 (Nitish, legal category 2, canon analytics-consent-model): ad_storage and
    // ad_user_data follow the same choice so signups reach Google Ads as conversions.
    // ad_personalization stays denied for everyone: measurement only, no remarketing.
    const quietGranted = analyticsConsent === false ? 'denied' : 'granted';
    quietBucket = true;
    gtag('consent', 'default', {
      'analytics_storage': quietGranted,
      'ad_storage': quietGranted,
      'ad_user_data': quietGranted,
      'ad_personalization': 'denied',
    });

  }

  consentModeInitialized = true;
}

/**
 * Update consent mode after user makes a choice
 * @param {boolean} analyticsGranted - Whether analytics was granted
 */
export function updateConsentMode(analyticsGranted) {
  if (!window.gtag) {
    console.warn('[GA4] Cannot update consent - gtag not initialized');
    return;
  }

  const state = analyticsGranted ? 'granted' : 'denied';
  const update = { 'analytics_storage': state };
  if (quietBucket) {
    // Quiet bucket: ad measurement follows the analytics choice (see initializeConsentMode).
    // Opt-in regions never get ad signals from the analytics banner.
    update['ad_storage'] = state;
    update['ad_user_data'] = state;
  }
  window.gtag('consent', 'update', update);

}

/**
 * Initialize Google Analytics
 * Call this once in your app's entry point (App.js)
 * 
 * @param {boolean} isEUUser - Whether user is in EU/EEA
 * @param {boolean} analyticsConsent - Whether user consented to analytics
 */
export function initGA(isEUUser = false, analyticsConsent = false) {
  // Skip if already initialized
  if (isInitialized) {
    return;
  }

  // For EU users without consent, don't initialize yet
  if (isEUUser && !analyticsConsent) {
    return;
  }

  // Skip in development if no measurement ID configured
  if (process.env.NODE_ENV === 'development' && !process.env.REACT_APP_GA_MEASUREMENT_ID) {
    return;
  }

  isInitialized = true;

  // Initialize Consent Mode first (if not already done)
  if (!consentModeInitialized) {
    initializeConsentMode(isEUUser, analyticsConsent);
  }

  // Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Configure GA4 via the dataLayer stub IMMEDIATELY (standard gtag pattern:
  // gtag.js processes the queue in order on load, so config always lands
  // before queued events). The old onload-config left a window where events
  // pushed during script load arrived before config and were dropped.
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    'send_page_view': false, // We'll track page views manually for SPA
    'debug_mode': process.env.NODE_ENV === 'development',
  });

  // Replay page views that arrived before GA was live (the buffered landing
  // view, see trackPageView) — restores landing + bounce accuracy.
  // Reset the dup-guard first: buffered entries recorded their key at
  // buffer time, and the guard would otherwise swallow the replay.
  lastPageViewKey = null;
  while (pendingPageViews.length > 0) {
    const pv = pendingPageViews.shift();
    trackPageView(pv.path, pv.title, pv.isLoggedIn);
  }
}

/**
 * Re-initialize GA4 after consent is granted (for EU users)
 * Called when EU user accepts cookies
 */
export function reinitializeGAAfterConsent() {
  if (isInitialized) {
    return;
  }

  // Load and initialize GA4
  initGA(true, true);
}

// One-shot navigation-source stamp. A UI element that wants its navigation
// attributed (e.g. the sidebar "New Agent" button) calls setNavSource('...')
// right before navigate(); the very next page_view carries it as nav_source.
let nextNavSource = null;
export function setNavSource(source) {
  nextNavSource = source;
}

// Page views that arrive BEFORE GA is live (geo-detection + consent init are
// async; the route effect fires immediately on landing). Buffered here and
// replayed from initGA — without this, the visit's FIRST page_view was
// silently dropped: landing views zeroed, bounce spiked to ~90% (Aug 3-4 bug).
let pendingPageViews = [];

// Consecutive-duplicate guard: the route effect re-fires for the SAME path
// when auth state settles (deps: [location.pathname, currentUser]), which
// double-counted page_views (observed live Aug 4). Key = path + auth-state;
// reset before replay in initGA.
let lastPageViewKey = null;

/**
 * Track page view
 * Call this on route changes in your SPA
 * 
 * @param {string} path - The page path (e.g., '/posts', '/chat/123')
 * @param {string} title - The page title
 * @param {boolean} isLoggedIn - Whether a user session is active (drives the
 *   /app-home virtual path + user_state param)
 */
export function trackPageView(path, title, isLoggedIn = false) {
  // Maintain SPA route memory regardless of consent/init state.
  // (Kept on the REAL path — source-page attribution should name actual routes.)
  if (path && path !== currentPath) {
    previousPath = currentPath;
    currentPath = path;
  }

  // One-shot nav-source: consumed by exactly the next page_view, then cleared,
  // so a deliberate UI click (e.g. sidebar "New Agent") is distinguishable from
  // a default arrival (login redirect, logo click, typed URL).
  // Consecutive-duplicate guard (see lastPageViewKey above).
  const pvKey = `${path}|${isLoggedIn}`;
  if (pvKey === lastPageViewKey) return;
  lastPageViewKey = pvKey;

  // GA not live yet? Buffer the view for replay from initGA (see
  // pendingPageViews above) instead of silently dropping it.
  if (!window.gtag || !isInitialized) {
    pendingPageViews.push({ path, title, isLoggedIn });
    if (pendingPageViews.length > 10) pendingPageViews.shift();
    return;
  }

  const navSource = nextNavSource;
  nextNavSource = null;

  // "/" is dual-use: marketing homepage (logged out) vs the app home state
  // (logged in). Report the logged-in view as the virtual path /app-home so
  // GA4 landing metrics count only true logged-out landings. All other app
  // surfaces already have real routes (/ai_employee, /projects, ...), so this
  // is the only virtual label needed. user_state makes any page's views
  // splittable by auth state in GA4 without changing real URLs.
  // SERIES SPLITS: 2026-09-12 → 09-16 (Nitish) /app-home was the New AI Employee page and New Chat
  // reported under /new; from the 2026-09-16 deploy on /app-home is New Chat again (the pre-09-12
  // series), New AI Employee reports under its real route /ai_employee, and /new only appears as a
  // redirect hop (it is an alias of "/").
  const isAppHome = isLoggedIn && path === '/';
  window.gtag('event', 'page_view', {
    page_path: isAppHome ? '/app-home' : path,
    page_title: title || document.title,
    page_location: isAppHome
      ? `${window.location.origin}/app-home`
      : window.location.href,
    user_state: isLoggedIn ? 'logged_in' : 'logged_out',
    // OS color-scheme preference (Aug 21 2026 — the CEL-1554 baseline read).
    system_theme: getSystemTheme(),
    // Home-screen app vs browser tab (Sep 30 2026, PWA item 1).
    display_mode: getDisplayMode(),
    ...(navSource ? { nav_source: navSource } : {}),
  });
}

/**
 * Which page sent the visitor to the CURRENT page. Prefers the in-app previous
 * route (SPA-aware), falls back to the external referrer, else 'direct'.
 * Used for source-page attribution on conversion events (e.g. sign_up).
 */
export function getAuthSourcePage() {
  if (previousPath) return previousPath;
  try {
    if (document.referrer) {
      const u = new URL(document.referrer);
      return u.origin === window.location.origin
        ? u.pathname
        : `external:${u.hostname}`;
    }
  } catch (e) {
    // Malformed referrer — fall through to 'direct'.
  }
  return 'direct';
}

/**
 * System (OS) theme preference — the raw prefers-color-scheme signal the
 * browser exposes. Distinct from resolved_theme (what the user SEES; logged-out
 * marketing is dark-locked today): this is what the visitor's OS is set to.
 * Baseline read for the light-mode initiative (CEL-1554). Anonymous — same
 * class as device type.
 */
export function getSystemTheme() {
  try {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
  } catch (e) { /* analytics must never break product flows */ }
  return 'unknown';
}

/**
 * How the app is running: 'standalone' = opened from a home-screen / installed-app icon
 * (display-mode standalone, or iOS Safari's navigator.standalone), 'browser' = a normal tab.
 * Sep 30 2026 (Nitish, PWA refinement item 1): tells us how many people use CellCog as a
 * home-screen app. Anonymous, same class as device type.
 */
export function getDisplayMode() {
  try {
    if (typeof window !== 'undefined') {
      if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return 'standalone';
      if (window.navigator && window.navigator.standalone === true) return 'standalone';
      return 'browser';
    }
  } catch (e) { /* analytics must never break product flows */ }
  return 'unknown';
}

// Events that arrive BEFORE PostHog is live (CEL-1830). The loader in
// index.html is consent/geo-gated and may boot posthog seconds-to-minutes
// after first paint (the verdict poll), so early events — the home hero
// section_view fires AT MOUNT — raced the loader and were silently dropped
// for PostHog (GA4 was safe: its dataLayer stub queues). Buffer them here and
// flush the moment posthog.capture exists. If PostHog never boots (consent
// denied, excluded identity), the poll expires and the buffer is discarded —
// exactly the old behavior, so this QUEUES allowed events without widening
// capture. Gates (route/identity/opt-out) are untouched: a flush into an
// opted-out instance is a no-op inside posthog-js itself.
let pendingPhEvents = [];
let phFlushPoll = null;

function phReady() {
  return !!(window.posthog && typeof window.posthog.capture === 'function');
}

function flushPendingPhEvents() {
  while (pendingPhEvents.length > 0) {
    const ev = pendingPhEvents.shift();
    try {
      window.posthog.capture(ev.name, ev.params);
    } catch (e) {
      // PostHog must never break product flows.
    }
  }
}

function startPhFlushPoll() {
  if (phFlushPoll) return;
  let ticks = 0;
  phFlushPoll = setInterval(() => {
    ticks += 1;
    if (phReady()) {
      clearInterval(phFlushPoll);
      phFlushPoll = null;
      flushPendingPhEvents();
    } else if (ticks > 660) {
      // A beat past the loader's own 600-tick consent poll: PostHog is not
      // coming this pageload. Drop the buffer (consent-denied/excluded paths
      // must stay uncaptured).
      clearInterval(phFlushPoll);
      phFlushPoll = null;
      pendingPhEvents = [];
    }
  }, 1000);
}

/**
 * Track custom event
 * Use for tracking user interactions
 * 
 * Dual-fires to GA4 (funnels/reporting) and PostHog. PostHog capture posture
 * (Aug 21 2026): marketing surfaces anonymous; signed-in REAL CUSTOMERS are
 * captured in-app too, identified by uid (AuthContext) — internal/demo
 * accounts are excluded at the loader, where their PostHog never loads.
 * 
 * @param {string} eventName - Event name (use snake_case)
 * @param {Object} params - Event parameters
 */
export function trackEvent(eventName, params = {}) {
  // resolved_theme (Aug 16, CEL-1287): stamp the RESOLVED display theme on
  // every event. ThemeContext mirrors it onto <html data-theme>, so this reads
  // what the user actually SEES (98% of users are on system preference, which
  // the DB cannot resolve). Logged-out marketing is dark-locked today; this
  // property is the baseline any future light-mode experiment reads. An
  // explicitly passed resolved_theme is never overridden.
  try {
    if (params.resolved_theme === undefined && typeof document !== 'undefined') {
      const ccTheme = document.documentElement.getAttribute('data-theme');
      if (ccTheme) params = { ...params, resolved_theme: ccTheme };
    }
  } catch (e) { /* analytics must never break product flows */ }

  // system_theme (Aug 21 2026, CEL-1554): stamp the OS color-scheme preference
  // on every event. An explicitly passed system_theme is never overridden.
  try {
    if (params.system_theme === undefined) {
      params = { ...params, system_theme: getSystemTheme() };
    }
    // display_mode (Sep 30 2026, PWA item 1): home-screen app vs browser tab, on every event.
    if (params.display_mode === undefined) {
      params = { ...params, display_mode: getDisplayMode() };
    }
  } catch (e) { /* analytics must never break product flows */ }

  if (window.gtag) {
    window.gtag('event', eventName, params);
  }

  if (phReady()) {
    // Preserve original order: anything still buffered goes out first.
    if (pendingPhEvents.length > 0) flushPendingPhEvents();
    try {
      window.posthog.capture(eventName, params);
    } catch (e) {
      // PostHog must never break product flows.
    }
  } else {
    // PostHog not live yet — queue instead of drop (see pendingPhEvents).
    pendingPhEvents.push({ name: eventName, params });
    if (pendingPhEvents.length > 50) pendingPhEvents.shift();
    startPhFlushPoll();
  }
}

/**
 * Pre-defined event trackers for common CellCog actions
 */
export const CellCogEvents = {
  // Authentication events (full taxonomy in js/cellcog/TRACKING.md).
  // sign_up / login fire on SUCCESS ONLY, keyed by what actually happened to
  // the ACCOUNT (new account = sign_up, existing = login) — regardless of
  // which page the user was standing on.
  signUp: (method = 'email', extra = {}) => trackEvent('sign_up', { method, ...extra }),
  login: (method = 'email', extra = {}) => trackEvent('login', { method, ...extra }),
  logout: () => trackEvent('logout'),

  // First engagement with an auth form (field focus or Google click), once per
  // page view. page: 'login'|'signup'; method: 'email'|'google'.
  authFormStart: (page, method) => trackEvent('auth_form_start', { page, method }),
  // An auth CTA was clicked (fires before validation — true click-through).
  authSubmit: (page, method) => trackEvent('auth_submit', { page, method }),
  // An auth attempt failed. error_code = Firebase error code.
  authError: (page, method, errorCode) => trackEvent('auth_error', {
    page,
    method,
    error_code: errorCode,
  }),
  // Visitor landed on /login or /signup already authenticated (instant redirect,
  // never saw the form) — explains pageview-vs-event gaps.
  authAlreadyLoggedIn: (page) => trackEvent('auth_redirect_logged_in', { page }),

  // Chat events
  chatCreated: (projectId = null) => trackEvent('chat_created', {
    project_id: projectId,
  }),
  chatMessageSent: (chatId, isFirstMessage = false) => trackEvent('chat_message_sent', {
    chat_id: chatId,
    is_first_message: isFirstMessage,
  }),

  // Project events
  projectCreated: (projectName) => trackEvent('project_created', {
    project_name: projectName,
  }),
  projectViewed: (projectId) => trackEvent('project_viewed', {
    project_id: projectId,
  }),

  // Post events
  postViewed: (postId, postType) => trackEvent('post_viewed', {
    post_id: postId,
    post_type: postType,
  }),
  postShared: (postId, shareMethod) => trackEvent('post_shared', {
    post_id: postId,
    share_method: shareMethod,
  }),

  // Conversion events
  ctaClicked: (ctaName, location) => trackEvent('cta_click', {
    cta_name: ctaName,
    location: location,
  }),
  pricingViewed: () => trackEvent('pricing_viewed'),
  subscriptionStarted: (planName) => trackEvent('subscription_started', {
    plan_name: planName,
  }),

  // Feature usage
  avatarCreated: () => trackEvent('avatar_created'),
  workflowCreated: () => trackEvent('workflow_created'),
  fileUploaded: (fileType) => trackEvent('file_uploaded', {
    file_type: fileType,
  }),

  // Cookie consent tracking
  cookieConsentChoice: (choice, isEU, country) => trackEvent('cookie_consent_choice', {
    choice: choice, // 'accept_all', 'reject_all', 'customize'
    is_eu_user: isEU,
    country: country,
  }),
};

export default {
  initGA,
  initializeConsentMode,
  updateConsentMode,
  reinitializeGAAfterConsent,
  trackPageView,
  trackEvent,
  getAuthSourcePage,
  CellCogEvents,
};
